import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, ShieldCheck, Printer } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney, vehicleLabel, exchangeLabel } from "@/lib/format";
import { printSaleReceipt } from "@/lib/paymentReceipt";

const METHOD_META = {
  cash: { label: "Cash", cls: "border-[#00713a] text-[#00713a]" },
  upi: { label: "UPI", cls: "border-[#5f3dc4] text-[#5f3dc4]" },
  online: { label: "Online", cls: "border-[#1971c2] text-[#1971c2]" },
  card: { label: "Card", cls: "border-[#E08600] text-[#A35700]" },
  platform: { label: "Platform", cls: "border-[#002FA7] text-[#002FA7]" },
};

const STATUS_META = {
  pending_approval: {
    label: "AWAITING VERIFICATION",
    cls: "border-amber-500 bg-amber-50 text-amber-700",
  },
  approved: {
    label: "VERIFIED",
    cls: "border-[#00B341] bg-[#00B341]/10 text-[#00B341]",
  },
  rejected: {
    label: "REJECTED",
    cls: "border-destructive bg-destructive/10 text-destructive",
  },
  cancelled: {
    label: "CANCELLED",
    cls: "border-ink bg-ink/10 text-ink",
  },
};

export function ApprovalStatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.approved;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 border text-[10px] font-mono uppercase tracking-wider ${meta.cls}`}
      data-testid={`approval-badge-${status}`}
    >
      {meta.label}
    </span>
  );
}

/**
 * Print ONE consolidated sale receipt covering every captured tender on the
 * sale (including payments that rolled over from a booking conversion).
 * Reconcile-due collections print on their own slip via ReconcileDueButton.
 */
let _dealershipCache = null;
async function getDealership() {
  if (_dealershipCache) return _dealershipCache;
  try {
    const r = await api.get("/settings/dealership");
    _dealershipCache = r.data || {};
  } catch {
    _dealershipCache = {};
  }
  return _dealershipCache;
}

export function PrintSaleReceiptButton({ sale, format = "a4", className = "" }) {
  const { user } = useAuth();
  const nonReconciledLines = (sale?.payment_lines || []).filter((l) => !l._reconciled);
  if (nonReconciledLines.length === 0) return null;

  const onClick = async (e) => {
    e?.stopPropagation();
    const dealership = await getDealership();
    const w = printSaleReceipt(sale, dealership, {
      format,
      receivedBy: sale.salesperson_name || user?.name || "",
    });
    if (!w) toast.message("Pop-ups blocked — allow pop-ups to print.");
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`print-sale-receipt-${sale.id}`}
      title="Print consolidated sale receipt"
      className={`inline-flex items-center justify-center p-1.5 border border-canvas-line hover:border-ink ${className}`}
    >
      <Printer size={12} weight="bold" />
    </button>
  );
}

export default function SaleApprovalActions({ sale, onChanged }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [verifyNote, setVerifyNote] = useState("");
  // Reject dialog state — split out from native window.prompt so we can also
  // surface the "Also cancel the linked booking" checkbox when applicable.
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [cancelBooking, setCancelBooking] = useState(false);
  // Admin-only cancellation of an already-approved sale.
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("customer_request");
  const [cancelNoteText, setCancelNoteText] = useState("");
  const [cancelBookingOnCancel, setCancelBookingOnCancel] = useState(true);

  const status = sale.approval_status || "approved";
  const isAdmin = user?.role === "admin";

  const canVerify = isAdmin || (user?.permissions || []).includes("sales.verify");
  const canReject = isAdmin || (user?.permissions || []).includes("sales.reject");
  const isOwnSale = sale.salesperson_id === user?.id;

  const submitVerify = async () => {
    const note = verifyNote.trim();
    if (!note) {
      toast.error("A verification note is required.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/sales/${sale.id}/approve`, { note });
      toast.success("Sale verified — registration pipeline started.");
      setShowVerifyDialog(false);
      setVerifyNote("");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitReject = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/sales/${sale.id}/reject`, {
        note: rejectNote,
        cancel_booking: cancelBooking,
      });
      toast.success(
        sale.booking_id
          ? cancelBooking
            ? "Sale rejected & booking cancelled — vehicle now available."
            : "Sale rejected — booking is now back to Active."
          : "Sale rejected."
      );
      setShowRejectDialog(false);
      setRejectNote("");
      setCancelBooking(false);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitCancel = async () => {
    if (busy) return;
    const note = cancelNoteText.trim();
    if (!note) {
      toast.error("A cancellation note is required.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/sales/${sale.id}/cancel`, {
        reason: cancelReason,
        note,
        cancel_booking: cancelBookingOnCancel,
      });
      toast.success("Approved sale cancelled — vehicle freed, ledgers reversed.");
      setShowCancelDialog(false);
      setCancelNoteText("");
      setCancelReason("customer_request");
      setCancelBookingOnCancel(true);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  // Admin-only "Cancel Approved Sale" is the only action available on
  // already-approved sales; everything else (Verify/Reject) is pending-only.
  if (status === "approved") {
    if (!isAdmin) return null;
    return (
      <>
        <button
          type="button"
          onClick={() => setShowCancelDialog(true)}
          disabled={busy}
          data-testid={`cancel-approved-sale-${sale.id}`}
          title="Cancel approved sale (admin only)"
          className="p-1.5 border border-destructive text-destructive hover:bg-destructive hover:text-white disabled:opacity-40 inline-flex items-center gap-1"
        >
          <X size={12} weight="bold" />
        </button>
        {showCancelDialog && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
            onClick={() => { if (!busy) { setShowCancelDialog(false); } }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-canvas-card border-2 border-destructive p-6 w-full max-w-md space-y-4"
              data-testid="cancel-approved-sale-dialog"
            >
              <div>
                <h3 className="text-lg font-bold text-ink">Cancel Approved Sale?</h3>
                <p className="text-xs text-ink-muted font-mono uppercase tracking-wider mt-1">
                  {sale.invoice_no || sale.vehicle?.vin || sale.id}
                </p>
              </div>
              <div className="border border-amber-400 bg-amber-50 p-3 text-[11px] text-amber-900 space-y-1">
                <div className="font-bold uppercase tracking-wider text-[10px]">This will:</div>
                <ul className="list-disc ml-4 space-y-0.5">
                  <li>Mark the sale as CANCELLED (visible in history)</li>
                  <li>Release the vehicle back to Available</li>
                  <li>Cancel the registration pipeline (if not completed)</li>
                  <li>Remove the sale from Finance / Exchange / Platform receivables</li>
                </ul>
                <div className="mt-2 pt-2 border-t border-amber-300 text-amber-900">
                  <b>Manual follow-up required:</b> Issue a refund payment voucher for any cash / UPI / DO already collected — those receipts stay on the audit trail.
                </div>
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                  Reason
                </label>
                <select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  data-testid="cancel-reason-select"
                  className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
                >
                  <option value="customer_request">Customer requested cancellation</option>
                  <option value="mistake">Booked in error / data mistake</option>
                  <option value="duplicate">Duplicate sale record</option>
                  <option value="financier_declined">Financier declined loan</option>
                  <option value="wrong_vin">Wrong VIN / vehicle mixed up</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                  Note <span className="text-destructive">*required</span>
                </label>
                <textarea
                  value={cancelNoteText}
                  onChange={(e) => setCancelNoteText(e.target.value)}
                  rows={3}
                  placeholder="Explain what happened + reference (e.g. call from customer, wrong VIN entered by Ramesh)…"
                  data-testid="cancel-note-input"
                  className={`w-full px-3 py-2 bg-canvas border text-sm focus:outline-none ${
                    cancelNoteText.trim() ? "border-canvas-line focus:border-ink" : "border-destructive focus:border-destructive"
                  }`}
                />
              </div>
              {sale.booking_id && (
                <label className="flex items-start gap-3 p-3 bg-canvas border border-canvas-line cursor-pointer hover:border-ink transition-colors">
                  <input
                    type="checkbox"
                    checked={cancelBookingOnCancel}
                    onChange={(e) => setCancelBookingOnCancel(e.target.checked)}
                    data-testid="cancel-approved-cancel-booking"
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-ink">Also cancel the linked booking</div>
                    <div className="text-[11px] text-ink-muted mt-0.5">
                      {cancelBookingOnCancel
                        ? "Booking will be marked cancelled and vehicle fully released."
                        : "Booking will be restored to Active — vehicle stays Reserved and the customer's advance is preserved."}
                    </div>
                  </div>
                </label>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setShowCancelDialog(false)}
                  disabled={busy}
                  className="px-4 py-2 text-sm border border-canvas-line hover:border-ink"
                  data-testid="cancel-approved-abort-btn"
                >
                  Keep Sale
                </button>
                <button
                  onClick={submitCancel}
                  disabled={busy || !cancelNoteText.trim()}
                  data-testid="cancel-approved-confirm-btn"
                  className="px-4 py-2 text-sm bg-destructive text-white hover:opacity-90 disabled:opacity-60"
                >
                  {busy ? "Cancelling…" : "Cancel Approved Sale"}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  if (status !== "pending_approval") return null;

  if (!canVerify && !canReject) return null;

  return (
    <>
      <div className="flex items-center gap-1.5">
        {canVerify && (
          <button
            type="button"
            onClick={() => setShowVerifyDialog(true)}
            disabled={busy || isOwnSale}
            data-testid={`verify-sale-${sale.id}`}
            title={isOwnSale ? "You can't verify your own sale (segregation of duties)" : "Verify sale"}
            className="p-1.5 border border-[#00B341] text-[#00B341] hover:bg-[#00B341] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
          >
            <ShieldCheck size={12} weight="bold" />
          </button>
        )}
        {canReject && (
          <button
            type="button"
            onClick={() => setShowRejectDialog(true)}
            disabled={busy}
            data-testid={`reject-sale-${sale.id}`}
            title="Reject sale"
            className="p-1.5 border border-destructive text-destructive hover:bg-destructive hover:text-white disabled:opacity-60"
          >
            <X size={12} weight="bold" />
          </button>
        )}
      </div>

      {showRejectDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
          onClick={() => { setShowRejectDialog(false); setRejectNote(""); setCancelBooking(true); }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-canvas-card border-2 border-destructive p-6 w-full max-w-md space-y-4"
            data-testid="reject-sale-dialog"
          >
            <div>
              <h3 className="text-lg font-bold text-ink">Reject Sale?</h3>
              <p className="text-xs text-ink-muted font-mono uppercase tracking-wider mt-1">
                {sale.vehicle?.vin || ""}
              </p>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
                Reason (optional)
              </label>
              <textarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                rows={3}
                placeholder="e.g. customer changed mind, finance fell through…"
                data-testid="reject-note-input"
                className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
              />
            </div>
            {sale.booking_id && (
              <label className="flex items-start gap-3 p-3 bg-canvas border border-canvas-line cursor-pointer hover:border-ink transition-colors">
                <input
                  type="checkbox"
                  checked={cancelBooking}
                  onChange={(e) => setCancelBooking(e.target.checked)}
                  data-testid="reject-cancel-booking"
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-ink">
                    Also cancel the linked booking
                  </div>
                  <div className="text-[11px] text-ink-muted mt-0.5">
                    {cancelBooking
                      ? "Vehicle will become Available. Handle the customer's advance separately."
                      : "Booking will be restored to Active — vehicle stays Reserved and the customer's advance is preserved."}
                  </div>
                </div>
              </label>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => { setShowRejectDialog(false); setRejectNote(""); setCancelBooking(false); }}
                disabled={busy}
                className="px-4 py-2 text-sm border border-canvas-line hover:border-ink"
                data-testid="reject-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={submitReject}
                disabled={busy}
                data-testid="reject-confirm-btn"
                className="px-4 py-2 text-sm bg-destructive text-white hover:opacity-90 disabled:opacity-60"
              >
                {busy ? "Rejecting…" : "Reject Sale"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showVerifyDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
          onClick={() => { setShowVerifyDialog(false); setVerifyNote(""); }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-canvas-card border-2 border-ink p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto space-y-4"
            data-testid="verify-sale-dialog"
          >
            <div className="flex items-center gap-2">
              <ShieldCheck size={18} weight="bold" className="text-[#00B341]" />
              <h2 className="font-display text-lg font-bold">Verify Sale</h2>
            </div>

            {/* Sale identity */}
            <div className="border border-canvas-line p-3 space-y-1 text-xs font-mono">
              <div><span className="text-ink-muted">Sale by:</span> <b>{sale.salesperson_name || "—"}</b></div>
              <div><span className="text-ink-muted">Customer:</span> <b>{sale.customer?.full_name || sale.customer?.name || "—"}</b></div>
              <div><span className="text-ink-muted">Vehicle:</span> <b>{vehicleLabel(sale.vehicle)} ({sale.vehicle?.vin})</b></div>
            </div>

            {/* Pricing summary */}
            <div className="grid grid-cols-4 gap-2" data-testid="verify-pricing-grid">
              <Stat label="On-Road" value={fmtMoney(sale.on_road_price || 0)} />
              <Stat label="Advance" value={fmtMoney(sale.advance || 0)} />
              <Stat label="Discount" value={fmtMoney(sale.discount || 0)} />
              <Stat label="Due" value={fmtMoney(sale.due || 0)} accent={Number(sale.due) > 0 ? "text-destructive" : "text-[#00713a]"} />
            </div>

            {/* Payment lines */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
                  Payment lines ({(sale.payment_lines || []).length})
                </div>
                <PrintSaleReceiptButton sale={sale} />
              </div>
              {(sale.payment_lines || []).length === 0 ? (
                <div className="border border-canvas-line p-3 text-xs text-ink-subtle italic">No payment lines recorded.</div>
              ) : (
                <table className="w-full text-xs border border-canvas-line" data-testid="verify-payment-lines">
                  <thead className="bg-canvas-panel border-b border-canvas-line">
                    <tr>
                      {["Method", "Amount", "Reference / Notes"].map((h) => (
                        <th
                          key={h}
                          className="text-left px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-muted"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(sale.payment_lines || []).map((pl, idx) => {
                      const meta = METHOD_META[pl.method] || { label: pl.method || "—", cls: "border-canvas-line text-ink" };
                      return (
                        <tr key={idx} data-testid={`verify-payment-row-${idx}`} className="border-b border-canvas-line last:border-b-0">
                          <td className="px-2 py-1.5">
                            <span className={`inline-flex px-1.5 py-0.5 border ${meta.cls} text-[10px] font-mono uppercase tracking-wider`}>
                              {meta.label}
                            </span>
                            {pl.method === "card" && pl.surcharge_amount > 0 && (
                              <span className="block font-mono text-[10px] text-ink-subtle mt-0.5">
                                +{fmtMoney(pl.surcharge_amount)} surcharge ({pl.surcharge_pct}%)
                              </span>
                            )}
                            {pl.method === "platform" && pl.platform_name && (
                              <span className="block font-mono text-[10px] text-[#002FA7] mt-0.5">
                                via {pl.platform_name}
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 font-mono tabular-nums">{fmtMoney(pl.amount || 0)}</td>
                          <td className="px-2 py-1.5 font-mono text-[11px]">
                            {pl.reference || <span className="text-ink-subtle italic">— no reference —</span>}
                            {pl.paid_on && <span className="block text-ink-subtle text-[10px]">on {pl.paid_on.slice(0, 10)}</span>}
                            {pl.receipt_no && (
                              <span className="block text-ink-subtle text-[10px] font-mono">{pl.receipt_no}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="bg-canvas-panel font-semibold">
                      <td className="px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider">Tendered total</td>
                      <td className="px-2 py-1.5 font-mono tabular-nums" data-testid="verify-tendered-total">
                        {fmtMoney((sale.payment_lines || []).reduce((s, p) => s + (Number(p.amount) || 0), 0))}
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>

            {/* Finance block */}
            {sale.has_finance && sale.finance && (
              <div className="border border-canvas-line p-3 space-y-1 text-xs font-mono" data-testid="verify-finance-block">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted mb-1">Finance</div>
                <div><span className="text-ink-muted">Financier:</span> <b>{sale.finance.financier_name || "—"}</b></div>
                <div><span className="text-ink-muted">Amount:</span> <b>{fmtMoney(sale.finance.finance_amount || sale.finance.amount || 0)}</b></div>
                {sale.finance.do_number && <div><span className="text-ink-muted">DO #:</span> <b>{sale.finance.do_number}</b></div>}
                {sale.finance.commission_amount > 0 && (
                  <div><span className="text-ink-muted">Commission:</span> <b>{fmtMoney(sale.finance.commission_amount)}</b></div>
                )}
              </div>
            )}

            {/* Exchange block */}
            {sale.has_exchange && sale.exchange && (
              <div className="border border-canvas-line p-3 space-y-1 text-xs font-mono" data-testid="verify-exchange-block">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted mb-1">Exchange (Trade-in)</div>
                <div><span className="text-ink-muted">Vehicle:</span> <b>{exchangeLabel(sale.exchange) || "—"} ({sale.exchange.registration_no || sale.exchange.registration_number || "no reg"})</b></div>
                <div><span className="text-ink-muted">Exchange value:</span> <b>{fmtMoney(sale.exchange.amount || sale.exchange.exchange_amount || 0)}</b></div>
                {sale.exchange.evaluator_name && <div><span className="text-ink-muted">Evaluator:</span> <b>{sale.exchange.evaluator_name}</b></div>}
              </div>
            )}

            {/* Platform receivable block */}
            {sale.platform_receivable && sale.platform_receivable.expected_amount > 0 && (
              <div className="border border-[#002FA7] bg-[#002FA7]/5 p-3 space-y-1 text-xs font-mono" data-testid="verify-platform-block">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#002FA7] mb-1">
                  Platform Receivable · {sale.platform_receivable.status?.toUpperCase()}
                </div>
                <div><span className="text-ink-muted">Expected from platform(s):</span> <b>{fmtMoney(sale.platform_receivable.expected_amount)}</b></div>
                {sale.platform_receivable.amount_received > 0 && (
                  <div><span className="text-ink-muted">Already settled:</span> <b className="text-[#00713a]">{fmtMoney(sale.platform_receivable.amount_received)}</b></div>
                )}
                {(sale.platform_receivable.breakdown || []).map((b, idx) => (
                  <div key={idx} className="text-[11px] text-ink-subtle">
                    · {b.platform_name} {fmtMoney(b.amount)} (ref: {b.reference || "—"})
                  </div>
                ))}
              </div>
            )}

            <p className="text-ink-muted text-xs">
              Confirm all payment transactions above have been reviewed against bank statements / UPI screenshots / card slips. A note explaining what you checked is required.
            </p>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                Verification Note <span className="text-destructive">*required</span>
              </label>
              <textarea
                value={verifyNote}
                onChange={(e) => setVerifyNote(e.target.value)}
                rows={3}
                data-testid="verify-note-input"
                className={`w-full px-3 py-2 bg-canvas border text-sm focus:outline-none ${
                  verifyNote.trim()
                    ? "border-canvas-line focus:border-ink"
                    : "border-destructive focus:border-destructive"
                }`}
                placeholder="e.g., All payment lines tally with bank statement / UPI app screenshots. Finance disbursement confirmed by SBI on 17-Jun."
              />
              {!verifyNote.trim() && (
                <p className="mt-1 text-[11px] text-destructive font-mono">
                  Type a short note describing what you reconciled — the Verify Sale button stays locked until you do.
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-canvas-line">
              <button
                type="button"
                onClick={() => { setShowVerifyDialog(false); setVerifyNote(""); }}
                className="px-3 py-2 border border-canvas-line text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitVerify}
                disabled={busy}
                data-testid="verify-submit-btn"
                className="px-4 py-2 bg-[#00B341] text-white text-sm font-semibold hover:bg-[#00713a] disabled:opacity-60 inline-flex items-center gap-2"
              >
                <Check size={14} weight="bold" />
                {busy ? "Verifying…" : "Verify Sale"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Stat({ label, value, accent = "text-ink" }) {
  return (
    <div className="border border-canvas-line p-2">
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted">{label}</div>
      <div className={`font-display text-sm font-bold tabular-nums mt-1 ${accent}`}>{value}</div>
    </div>
  );
}
